import ObjectId from 'bson-objectid';
import { Avatar, Button, Clickable, Col, Dropdown, Icon, Input, Row, Text } from 'components';
import { WebsiteView } from 'components/Post/components';
import { Api, formatText, theme, uploadMedia } from 'lib';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { DatePicker, IrreversableModal, SearchUsers } from 'screens/Admin/components';
import { LinkInput, Media, Youtube } from './';

// Either Post or News View
const Post = ({ post, setDataId, callback, closeModal, isEditing, isProcessing, setUploading }) => {
  const [owner, setOwner] = useState(null),
    [caption, setCaption] = useState(''),
    [media, setMedia] = useState([]),
    [metaData, setMetaData] = useState(null),
    [youtubeId, setYoutubeId] = useState(false),
    [metaLoading, setMetaLoading] = useState(false),
    [createdAt, setCreatedAt] = useState(''),
    [publishingDisabled, setPublishingDisabled] = useState(false),
    [editDisplayDisabled, setEditDisplayDisabled] = useState(false),
    [isNews, setIsNews] = useState(false),
    [errors, setErrors] = useState([]),
    [showDeleteModal, setShowDeleteModal] = useState(false),
    [deleting, setDeleting] = useState(false),
    displayOptions = ['Display as normal post', 'Display in Your Views on the News'],
    dispatch = useDispatch();

  async function handleDelete() {
    setDeleting(true);
    const res = await Api.post('/admin/removeData', { type: 'Post', id: post._id });
    setDeleting(false);
    if (!res) return;
    closeModal();
  }

  useEffect(() => {
    if (post) {
      setOwner(post?.owner || null);
      setCreatedAt(new Date(post?.createdAt) || null);
      setYoutubeId(post?.youtubeId || null);
      setCaption(post?.caption || '');
      setMedia(post?.media?.length ? post.media : []);
      setMetaData(post.metaData || null);
      setEditDisplayDisabled(true);
      setIsNews(!post.isPost || false);
    }

    if (isProcessing) {
      setPublishingDisabled(true);
    }
  }, []);

  useEffect(() => {
    setPublishingDisabled(!isProcessing);
  }, [isProcessing]);

  useEffect(() => {
    setPublishingDisabled(isNews && isNewsDisabled());
  }, [isNews, youtubeId, media, metaData]);

  function isNewsDisabled() {
    return !media?.length && !youtubeId && !metaData;
  }

  function displayNewsCaption() {
    return isNews ? displayOptions[1] : displayOptions[0];
  }

  async function submit() {
    dispatch({
      type: 'LOADING_BUTTON',
      payload: 'createPost'
    });

    const newErrs = [];
    if (!owner) newErrs.push({ input: 'owner', err: 'Required' });

    if (isNewsDisabled() && !caption) {
      newErrs.push({
        input: 'media',
        err: 'At least one type of media or caption text is required'
      });
    }

    if (newErrs.length) {
      dispatch({
        type: 'LOADING_BUTTON',
        payload: null
      });

      return setErrors(newErrs);
    }

    const formatted = formatText(caption);

    const tags = formatted.filter((x) => x.type === 'tag').map((x) => x.text),
      mentioned = formatted.filter((x) => x.type === 'mention').map((x) => x.text);

    if (media[0]?.file) return handleVideoPost(tags, mentioned, isNews);
    if (isEditing) return edit();

    const uploadedMedia = await Promise.all(media.map((x, i) => uploadMedia(x, i)));

    const data = {
      caption,
      metaData,
      tags,
      mentioned,
      media: uploadedMedia,
      owner: owner._id,
      createdAt: createdAt ? new Date(createdAt) : new Date(),
      youtubeId: youtubeId || '',
      ownerType: owner.type
    };

    const endpoint = isNews ? '/admin/createNewsView' : '/admin/createPost';
    const res = await Api.post(endpoint, data);

    dispatch({
      type: 'LOADING_BUTTON',
      payload: null
    });

    if (!res) {
      return;
    }

    data.isPost = !isNews;

    dispatch({
      type: 'ADMIN_ADD_POST',
      payload: { ...data, owner, _id: res._id }
    });

    dispatch({
      type: 'HANDLE_RES',
      payload: { res: isNews ? 'Your Views on the News post created.' : 'Post created.' }
    });

    setEditDisplayDisabled(true);
    setDataId(res._id);
  }

  async function edit() {
    dispatch({
      type: 'LOADING_BUTTON',
      payload: 'createPost'
    });

    const uploadedMedia = await Promise.all(
      media.map((x, i) => {
        if (!x.preview) return x;

        return uploadMedia(x, i);
      })
    );

    const formatted = formatText(caption);

    const tags = formatted.filter((x) => x.type === 'tag').map((x) => x.text),
      mentioned = formatted.filter((x) => x.type === 'mention').map((x) => x.text);

    let data = {
      caption,
      metaData,
      tags,
      mentioned,
      media: uploadedMedia,
      owner: owner._id,
      createdAt: createdAt ? new Date(createdAt) : new Date(),
      youtubeId: youtubeId || '',
      ownerType: owner.type
    };

    let res;

    if (isNews) {
      res = await Api.put('/admin/editNewsView/' + post._id, data);
    } else {
      data.postId = post._id;
      res = await Api.post('/admin/editPost', data);
    }

    dispatch({
      type: 'LOADING_BUTTON',
      payload: null
    });

    if (!res) {
      return;
    }

    dispatch({
      type: 'ADMIN_EDIT_POST',
      payload: res
    });

    dispatch({
      type: 'HANDLE_RES',
      payload: { res: isNews ? 'Your Views on the News post updated.' : 'Post updated.' }
    });
  }

  async function handleVideoPost(tags, mentioned, isNews) {
    setUploading(true);
    let data = new FormData();
    const fileId = ObjectId().toString();

    data.append('owner', owner._id);
    data.append('createdAt', createdAt ? new Date(createdAt) : new Date());
    data.append('ownerType', owner.type);
    data.append('caption', caption);
    data.append('tags', JSON.stringify(tags));
    data.append('mentioned', JSON.stringify(mentioned));
    if (isEditing) data.append('postId', post._id);

    const file = media[0].file;
    const fileType = file.type.split('/')[1];
    const fileName = `${fileId}-1.${fileType}`;

    data.append('fileName', fileName);
    data.append('video', file, fileName);

    const createEndpoint = isNews ? '/admin/createNewsView' : '/admin/createPost';
    const editEndpoint = isNews ? '/admin/editNewsView' : '/admin/editPost';
    let res;

    if (isEditing) {
      if (isNews) {
        res = await Api.put(editEndpoint + '/' + post._id, data);
      } else {
        data.postId = post._id;
        res = await Api.post(editEndpoint, data);
      }
    } else {
      res = await Api.post(createEndpoint, data);
    }

    setUploading(false);
    dispatch({
      type: 'LOADING_BUTTON',
      payload: null
    });

    if (!res) {
      return;
    }

    dispatch({
      type: isEditing ? 'ADMIN_EDIT_POST' : 'ADMIN_ADD_POST',
      payload: { ...res, owner }
    });

    dispatch({
      type: 'HANDLE_RES',
      payload: { res: 'Upload successful. Processing your video.' }
    });

    setDataId(res._id);
  }

  return (
    <Col start between ht="calc(100% - 55px)" pad="0 24px">
      <Col wid="100%" noFlex>
        <Row between marg="24px 0 0">
          <Text marg="0 16px 4px" b2 med></Text>
        </Row>
        {editDisplayDisabled ? (
          <Input
            wid="100%"
            disabled
            defaultValue={displayNewsCaption()}
            bg={'rgba(121, 137, 160, 0.2)'}
          />
        ) : (
          <Dropdown
            title=""
            admin
            options={displayOptions}
            selected={displayNewsCaption()}
            handleSelect={(e) => {
              setIsNews(e === displayOptions[1]);
            }}
            bg="#fff"
            rounded
            wid="100%"
          />
        )}
        <Row between marg="24px 0 0">
          <Text marg="0 16px 4px" b2 med>
            Owner
          </Text>
        </Row>
        {owner && (
          <Row noFlex start marg="0 0 0">
            <Row center between wid="100%" pad="0 0">
              <Row noFlex center>
                <Avatar small user={owner} />

                <Col marg="0 0 0 8px" noFlex>
                  <Text>{owner?.name}</Text>

                  <Text c1 col={theme.GREY_60}>
                    @{owner?.username}
                  </Text>
                </Col>
              </Row>

              <Clickable
                onClick={() => {
                  setOwner(null);
                }}
              >
                <Icon type="close" dimensions={14} />
              </Clickable>
            </Row>
          </Row>
        )}

        <SearchUsers
          selected={owner}
          handleSelect={setOwner}
          noRounded
          error={errors.find((x) => x.input === 'owner')}
        />

        <Col marg="0 0 0" wid="100%">
          {metaData && (
            <WebsiteView
              isAdmin
              metaData={metaData}
              dimensions={552}
              remove={() => setMetaData(null)}
            />
          )}

          {!youtubeId && !metaData && (
            <Media
              media={media}
              setMedia={setMedia}
              isEditing={isEditing}
              isProcessing={isProcessing}
            />
          )}

          {!media?.length && !metaData && youtubeId && (
            <Youtube youtubeId={youtubeId} setYoutubeId={setYoutubeId} isEditing={isEditing} />
          )}

          {!media?.length && !metaData && !youtubeId && (
            <LinkInput
              metaData={metaData}
              youtubeId={youtubeId}
              setMetaData={setMetaData}
              setYoutubeId={setYoutubeId}
              setLoading={setMetaLoading}
              loading={metaLoading}
              err={errors.find((x) => x.input === 'media')}
            />
          )}
        </Col>

        <Row marg="24px 0 0" pos="relative">
          <Input
            title="Caption"
            wid="100%"
            multiline
            placeholder="Caption..."
            ht="100px"
            emoji
            value={caption}
            onChange={({ target }) => setCaption(target.value)}
            error={errors.find((x) => x.input === 'media')?.err}
          />
        </Row>

        <Text marg="24px 16px 4px" b2 med>
          Publish date
        </Text>

        <DatePicker
          selected={createdAt}
          handleSelect={setCreatedAt}
          title={!createdAt && 'Now'}
          marg="0 16px 0"
        />
      </Col>

      <Row pad="54px 0 24px">
        <Button big text="Cancel" onClick={closeModal} marg="0 24px 0 0" bordered />

        {isEditing && (
          <Button
            big
            text="Delete"
            onClick={() => setShowDeleteModal(true)}
            marg="0 24px 0 0"
            bg={theme.CORAL}
            disabled={deleting}
          />
        )}

        <Button
          big
          onClick={submit}
          disabled={publishingDisabled}
          text={isEditing ? 'Edit' : 'Publish'}
          bg={theme.SECONDARY}
          action="createPost"
        />
      </Row>

      <IrreversableModal
        visible={showDeleteModal}
        heading="You are about to remove this post."
        onConfirm={handleDelete}
        closeModal={() => setShowDeleteModal(false)}
      />
    </Col>
  );
};

export default Post;
